/**
 * withBackboneModels HOC
 *
 * This HOC is a replacement for BackboneMixin which is used to re-render
 * a component when props that backbone Models or Collections are updated.
 *
 * Usage:
 *   type Props = {|
 *       model: Model,
 *   |};
 *
 *   class WrappedComponent extends React.Component<Props> { ... }
 *
 *   const Component = withBackboneModels(WrappedComponent, props => [props.model]);
 */

import * as React from "react";

import type Backbone from "#shared-package/backbone-lite.js";

type Props = {
    children: () => React.ReactNode;
    models: Array<Backbone.Model>;
};

type State = Record<any, any>;

/**
 * BackboneModelProvider
 *
 * This private component does all of the heavy lifting.  It registers listeners
 * for changes to the Models or Collections passed to it via the `models` prop.
 * In response to any changes it will re-render `children`.
 */
class BackboneModelProvider extends React.Component<Props, State> {
    _isMounted: boolean;
    _backboneModels: Array<Backbone.Model>;

    constructor(props: Props) {
        super(props);
        this._isMounted = false;
        // store these so we can unbind them when the component is unmounted
        this._backboneModels = props.models;
        this._bind(props.models);
    }

    componentDidMount() {
        this._isMounted = true;
    }

    // The backbone models may have changed - rebind to the new ones
    componentDidUpdate(nextProps: Props, nextState: State) {
        const previousModels = this.props.models;
        const currentModels = nextProps.models;
        // update these so we can unbind them when the component is unmounted
        this._backboneModels = currentModels;

        const oldModels: Array<Backbone.Model> = [];
        const newModels: Array<Backbone.Model> = [];

        for (const model of previousModels) {
            if (currentModels.indexOf(model) < 0) {
                oldModels.push(model);
            }
        }

        for (const model of currentModels) {
            if (previousModels.indexOf(model) < 0) {
                newModels.push(model);
            }
        }

        this._unbind(oldModels);
        this._bind(newModels);
    }

    componentWillUnmount() {
        this._isMounted = false;
        this._unbind(this._backboneModels);
    }

    _bind(models: Array<Backbone.Model>) {
        models.map((model: Backbone.Model) =>
            model.on("add change remove reset", this._backboneForceUpdate),
        );
    }

    _unbind(models: Array<Backbone.Model>) {
        models.map((model: Backbone.Model) =>
            model.off("add change remove reset", this._backboneForceUpdate),
        );
    }

    _backboneForceUpdate = () => {
        // TODO(joel): more rigorous fix needed? -- for the following error:
        // "Invariant Violation: forceUpdate(...): Can only force an update on
        // mounted or mounting components."
        if (this._isMounted) {
            this.forceUpdate();
        }
    };

    render(): React.ReactNode {
        return this.props.children();
    }
}

const withBackboneModel =
    (
        // BackboneModelProvider will monitor each Model and Collection returned
        // by this function and re-render the component when any changes occur.
        // NOTE(kevinb): TypeScript can't propagate the interferred valued of
        // type params from the inner function to the outer one so we end up having
        // to type `props` as `any` here.
        propsToModels: (props: any) => Array<Backbone.Model>,
    ) =>
    <
        C extends React.ComponentType<React.ComponentProps<C>> & {
            displayName?: string | undefined;
        },
        OuterProps = JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
    >(
        WrappedComponent: C,
    ): React.ComponentType<OuterProps> => {
        const ComponentWithBackboneModels = (props: OuterProps) => (
            <BackboneModelProvider models={propsToModels(props)}>
                {() => <WrappedComponent {...(props as any)} />}
            </BackboneModelProvider>
        );

        // Try to create a nice displayName for React Dev Tools.
        const displayName =
            WrappedComponent.displayName ||
            WrappedComponent.name ||
            "Component";

        ComponentWithBackboneModels.displayName = `withBackboneModels(${displayName})`;

        return ComponentWithBackboneModels;
    };

export default withBackboneModel;
